import React, { useState } from "react";
import { useAICopilotBannerViewModel } from "./useAICopilotBannerViewModel";
import {
	AIBadge,
	AIBadgeButton,
	AIBadgeButtonContainer,
	AnalysisSkeleton,
	AnalysisText,
	BannerBody,
	BannerContainer,
	BannerHeader,
	EmptyLeftColumn,
	EmptyRightColumn,
	EmptyStateGrid,
	EmptySubtitle,
	EmptyTextContainer,
	EmptyTitle,
	ErrorText,
	FilledGreenButton,
	LeftCTARow,
	MetricCard,
	MetricLabel,
	MetricNumber,
	MetricsGrid,
	MetricUnit,
	MetricValue,
	OutlineDarkButton,
	PatientHeaderCard,
	PatientInfoWrapper,
	ListAvatar,
	ListPhoto,
	PatientTextWrapper,
	PatientName,
	PatientSubtext,
	ChevronWrapper,
	RightColumn,
} from "./styles";
import PatientListSelectionModal from "../../OperationalDashboard/components/PatientListSelectionModal";
import ReadyForReevaluationModal from "../../OperationalDashboard/components/ReadyForReevaluationModal";
import CriticalAlertModal from "../../OperationalDashboard/components/CriticalAlertModal";
import PatientSelectionForCopilotModal from "../../OperationalDashboard/components/PatientSelectionForCopilotModal";
import DetailedRecordModal from "../../OperationalDashboard/components/DetailedRecordModal";
import {
	CopilotSummarySnapshot,
	useAICopilotViewModel,
} from "../../OperationalDashboard/components/AICopilotSection/useAICopilotViewModel";
import { ReactComponent as StarIcon } from "../../../assets/kinologyIcons/svg/Star4.svg";
import { ReactComponent as PlayIcon } from "../../../assets/kinologyIcons/svg/play_circle_white.svg";
import { ReactComponent as ChevronIcon } from "../../../assets/kinologyIcons/svg/arrow-right-radius.svg";
import { ReactComponent as OpenCardIcon } from "../../../assets/kinologyIcons/svg/icon-container-open.svg";
import { ReactComponent as CloseCardIcon } from "../../../assets/kinologyIcons/svg/icon-container-close.svg";
import { ReactComponent as SendIcon } from "../../../assets/kinologyIcons/svg/send.svg";
import { ReactComponent as PatientIcon } from "../../../assets/kinologyIcons/svg/frame_person.svg";

const AICopilotBanner: React.FC = () => {
	const {
		isV1Mode,
		hasOperationalDashboardAccess,
		isEmptyState,
		activePatient,
		setSelectedPatient,
		metrics,
		summaryLoading,
		summaryError,
		retrySummary,
		isPatientAreaLoading,
		patientAreaError,
		handleStartFirstEvaluation,
		handleGoToPanel,
		handleGoToPatientArea,
		isPatientListModalOpen,
		setIsPatientListModalOpen,
		handleOpenPatientListModal,
	} = useAICopilotBannerViewModel();
	const [isOpenCard, setIsOpenCard] = useState(true);
	const [isReevaluationModalOpen, setIsReevaluationModalOpen] = useState(false);
	const [isCriticalAlertModalOpen, setIsCriticalAlertModalOpen] =
		useState(false);
	const [isCopilotPatientModalOpen, setIsCopilotPatientModalOpen] =
		useState(false);
	const [isDetailedRecordModalOpen, setIsDetailedRecordModalOpen] =
		useState(false);
	const [copilotSummarySnapshot, setCopilotSummarySnapshot] =
		useState<CopilotSummarySnapshot | null>(null);

	const {
		patient,
		summaryText,
		contentLoading,
		contentError,
		sendingReport,
		reportError,
		handleSendReport,
		handleRetryContent,
	} = useAICopilotViewModel(
		activePatient,
		() => setIsDetailedRecordModalOpen(true),
		setCopilotSummarySnapshot
	);

	if (isV1Mode || !hasOperationalDashboardAccess) return null;

	const isInitialLoading = summaryLoading && !activePatient;

	return (
		<>
			{isEmptyState && !isInitialLoading ? (
				<BannerContainer>
					<EmptyStateGrid>
						<EmptyLeftColumn>
							<AIBadgeButton>
								<StarIcon width="10px" height="10px" />
								Copiloto Clínico IA
							</AIBadgeButton>
							<EmptyTextContainer>
								<EmptyTitle>Bem-vindo à Kinology</EmptyTitle>
								<EmptySubtitle>Inicie a sua primeira avaliação</EmptySubtitle>
							</EmptyTextContainer>
						</EmptyLeftColumn>
						<EmptyRightColumn>
							<FilledGreenButton
								type="button"
								onClick={handleStartFirstEvaluation}
							>
								<PlayIcon width={14} height={14} />
								Iniciar Primeira Avaliação
							</FilledGreenButton>
							{hasOperationalDashboardAccess && (
								<OutlineDarkButton type="button" onClick={handleGoToPanel}>
									Ver meu painel
									<ChevronIcon width="12" height="12" />
								</OutlineDarkButton>
							)}
						</EmptyRightColumn>
					</EmptyStateGrid>
				</BannerContainer>
			) : (
				<>
					<BannerHeader $isOpenCard={isOpenCard}>
						<AIBadge>
							<AIBadgeButton>
								<StarIcon width="10px" height="10px" />
								Copiloto Clínico IA
							</AIBadgeButton>
							<div>
								<FilledGreenButton
									type="button"
									onClick={handleOpenPatientListModal}
								>
									<PlayIcon width={16} height={16} />
									Iniciar Nova Avaliação
								</FilledGreenButton>
								<AIBadgeButtonContainer
									type="button"
									onClick={() => setIsOpenCard((open) => !open)}
									aria-label={
										isOpenCard ? "Recolher Copiloto" : "Expandir Copiloto"
									}
									aria-expanded={isOpenCard}
								>
									{isOpenCard ? <OpenCardIcon /> : <CloseCardIcon />}
								</AIBadgeButtonContainer>
							</div>
						</AIBadge>
					</BannerHeader>

					{isOpenCard && (
						<BannerBody>
							<div>
								{patient ? (
									<PatientHeaderCard
										onClick={() => setIsCopilotPatientModalOpen(true)}
										role="button"
										tabIndex={0}
										onKeyDown={(event) => {
											if (event.key === "Enter" || event.key === " ") {
												event.preventDefault();
												setIsCopilotPatientModalOpen(true);
											}
										}}
									>
										<PatientInfoWrapper>
											<ListAvatar $hasPhoto={!!patient.photoUrl}>
												{patient.photoUrl ? (
													<ListPhoto
														src={patient.photoUrl}
														alt={patient.name}
													/>
												) : (
													patient.initials
												)}
											</ListAvatar>
											<PatientTextWrapper>
												<PatientName>{patient.name}</PatientName>
												<PatientSubtext>
													Paciente avaliado em {patient.evaluationDate}
												</PatientSubtext>
											</PatientTextWrapper>
										</PatientInfoWrapper>
										<ChevronWrapper
											role="button"
											tabIndex={0}
											aria-label="Abrir prontuário detalhado"
											onClick={(event) => {
												event.stopPropagation();
												setIsDetailedRecordModalOpen(true);
											}}
											onKeyDown={(event) => {
												if (event.key === "Enter" || event.key === " ") {
													event.preventDefault();
													event.stopPropagation();
													setIsDetailedRecordModalOpen(true);
												}
											}}
										>
											<ChevronIcon width="14" height="14" />
										</ChevronWrapper>
									</PatientHeaderCard>
								) : isInitialLoading ? (
									<AnalysisSkeleton aria-label="Carregando último paciente" />
								) : summaryError ? (
									<ErrorText role="alert">
										{summaryError}
										<button type="button" onClick={retrySummary}>
											Tentar novamente
										</button>
									</ErrorText>
								) : null}

								{contentLoading ? (
									<AnalysisSkeleton aria-label="Carregando análise do Copiloto" />
								) : contentError ? (
									<ErrorText role="alert">
										{contentError}
										<button type="button" onClick={handleRetryContent}>
											Tentar novamente
										</button>
									</ErrorText>
								) : summaryText ? (
									<AnalysisText>
										<strong>Análise do Copiloto:</strong> {summaryText}
									</AnalysisText>
								) : null}

								{activePatient && (
									<LeftCTARow>
										<FilledGreenButton
											type="button"
											onClick={handleSendReport}
											disabled={sendingReport}
										>
											<SendIcon width="12" height="12" />
											{sendingReport ? "Carregando..." : "Enviar Relatório"}
										</FilledGreenButton>
										<OutlineDarkButton
											type="button"
											onClick={handleGoToPatientArea}
											disabled={isPatientAreaLoading}
										>
											<PatientIcon width="14" height="14" />
											{isPatientAreaLoading
												? "Carregando..."
												: "Ver área do paciente"}
										</OutlineDarkButton>
									</LeftCTARow>
								)}
								{(reportError || patientAreaError) && (
									<ErrorText role="alert">
										{reportError || patientAreaError}
									</ErrorText>
								)}
							</div>

							<RightColumn>
								<MetricsGrid>
									<MetricCard
										type="button"
										onClick={() => setIsReevaluationModalOpen(true)}
									>
										<MetricLabel>Prontos p/ reavaliar</MetricLabel>
										<MetricValue>
											<MetricNumber>{metrics.reavaliacao}</MetricNumber>
											<MetricUnit>Pacientes</MetricUnit>
										</MetricValue>
									</MetricCard>
									<MetricCard
										alert
										type="button"
										onClick={() => setIsCriticalAlertModalOpen(true)}
									>
										<MetricLabel alert>Clientes inativos</MetricLabel>
										<MetricValue alert>
											<MetricNumber alert>{metrics.inativos}</MetricNumber>
											<MetricUnit alert>Pacientes</MetricUnit>
										</MetricValue>
									</MetricCard>
								</MetricsGrid>
								{hasOperationalDashboardAccess && (
									<OutlineDarkButton type="button" onClick={handleGoToPanel}>
										Ver meu painel
										<ChevronIcon width="12" height="12" />
									</OutlineDarkButton>
								)}
							</RightColumn>
						</BannerBody>
					)}
				</>
			)}

			<ReadyForReevaluationModal
				isOpen={isReevaluationModalOpen}
				onClose={() => setIsReevaluationModalOpen(false)}
			/>
			<CriticalAlertModal
				isOpen={isCriticalAlertModalOpen}
				onClose={() => setIsCriticalAlertModalOpen(false)}
			/>
			<PatientListSelectionModal
				isOpen={isPatientListModalOpen}
				onClose={() => setIsPatientListModalOpen(false)}
			/>
			<PatientSelectionForCopilotModal
				isOpen={isCopilotPatientModalOpen}
				onClose={() => setIsCopilotPatientModalOpen(false)}
				onSelectPatient={setSelectedPatient}
			/>
			<DetailedRecordModal
				isOpen={isDetailedRecordModalOpen}
				onClose={() => setIsDetailedRecordModalOpen(false)}
				patient={activePatient}
				copilotSummary={copilotSummarySnapshot}
			/>
		</>
	);
};

export default AICopilotBanner;
